/**
 * DiscountEngine.js (Improved version with robust error handling)
 * Immutable discount state management and priority sorting with defensive programming
 */

export default class DiscountEngine {
    constructor(logger = null) {
        this.logger = logger || console;
    }

    /**
     * Sort discounts by priority with comprehensive error handling
     * @param {Array} discounts - Array of discount objects
     * @returns {Array} Sorted array of discounts (new array, original unchanged)
     */
    sortDiscountsByPriority(discounts) {
        // Input validation
        if (!discounts || !Array.isArray(discounts)) {
        this.logger.warn('sortDiscountsByPriority: Invalid input, returning empty array');
        return [];
        }

        if (discounts.length === 0) {
        return [];
        }

        // Filter out null/undefined items and create defensive copy
        const validDiscounts = discounts.filter(discount =>
        discount !== null &&
        discount !== undefined &&
        typeof discount === 'object'
        );

        if (validDiscounts.length === 0) {
        this.logger.warn('sortDiscountsByPriority: No valid discounts found');
        return [];
        }

        return [...validDiscounts].sort((a, b) => {
        try {
            // Defensive null checks
            if (!a && !b) return 0;
            if (!a) return 1;
            if (!b) return -1;

            // Numeric priority first (higher number = higher priority)
            const priorityA = this.getNumericPriority(a);
            const priorityB = this.getNumericPriority(b);

            if (priorityA !== null && priorityB !== null && priorityA !== priorityB) {
            return priorityB - priorityA; // Higher priority first
            }

            // Override discounts always come first
            const isOverrideA = this.isOverride(a);
            const isOverrideB = this.isOverride(b);
            if (isOverrideA !== isOverrideB) {
            return isOverrideA ? -1 : 1;
            }

            // Non-combinable discounts get priority
            const nonCombinableA = this.isNonCombinable(a);
            const nonCombinableB = this.isNonCombinable(b);
            if (nonCombinableA !== nonCombinableB) {
            return nonCombinableA ? -1 : 1;
            }

            // Logical priority based on discount characteristics
            const logicalA = this.getLogicalPriority(a);
            const logicalB = this.getLogicalPriority(b);
            return logicalA - logicalB; // Lower number = higher priority

        } catch (error) {
            this.logger.error('Error during discount comparison:', error);
            return 0; // Maintain order on error
        }
        });
    }

    /**
     * Resolve conflicts between non-combinable discounts
     * @param {Array} discounts - Pre-sorted discounts
     * @param {Set} previouslyApplied - Set of previously applied discount codes
     * @returns {Array} Resolved discounts without conflicts
     */
    resolveNonCombinableConflicts(discounts, previouslyApplied = new Set()) {
        if (!discounts || !Array.isArray(discounts) || discounts.length === 0) {
        return [];
        }

        const result = [];
        const processed = new Set();
        const blockedServices = new Set();

        try {
        for (let i = 0; i < discounts.length; i++) {
            if (processed.has(i)) continue;

            const discount = discounts[i];
            if (!discount || typeof discount !== 'object') {
            processed.add(i);
            continue;
            }

            const isNonCombinable = this.isNonCombinable(discount);

            if (!isNonCombinable) {
            result.push(discount);
            processed.add(i);
            continue;
            }

            // Find conflicts with other non-combinable discounts
            const conflictingIndices = [];
            const discountServices = this.getDiscountServices(discount);

            // Check if any services are already blocked by higher priority non-combinable discounts
            const hasBlockedService = discountServices.some(service => blockedServices.has(service));

            if (hasBlockedService) {
            this.logger.info(`Discount ${this.getDiscountCode(discount)} blocked by higher priority non-combinable discount`);
            processed.add(i);
            continue;
            }

            // Look for conflicts with remaining discounts
            for (let j = i + 1; j < discounts.length; j++) {
            if (processed.has(j)) continue;

            const other = discounts[j];
            if (!other || !this.isNonCombinable(other)) continue;

            const otherServices = this.getDiscountServices(other);
            if (this.servicesOverlap(discountServices, otherServices)) {
                conflictingIndices.push(j);
            }
            }

            if (conflictingIndices.length === 0) {
            result.push(discount);
            processed.add(i);

            // Block services for lower priority discounts
            discountServices.forEach(service => {
                if (service !== 'ORDER_LEVEL') {
                blockedServices.add(service);
                }
            });
            continue;
            }

            // Resolve conflict by choosing the best discount
            const allConflicting = [i, ...conflictingIndices];
            const conflictingDiscounts = allConflicting
            .map(idx => discounts[idx])
            .filter(d => d && typeof d === 'object');

            if (conflictingDiscounts.length > 0) {
            const winner = this.chooseBestDiscount(conflictingDiscounts, previouslyApplied);
            if (winner) {
                result.push(winner);

                // Block services
                const winnerServices = this.getDiscountServices(winner);
                winnerServices.forEach(service => {
                if (service !== 'ORDER_LEVEL') {
                    blockedServices.add(service);
                }
                });
            }
            }

            allConflicting.forEach(idx => processed.add(idx));
        }

        } catch (error) {
        this.logger.error('Error resolving non-combinable conflicts:', error);
        // Return whatever we managed to process
        }

        return result;
    }

    /**
     * Choose the best discount among conflicting ones
     * @param {Array} conflictingDiscounts - Array of conflicting discounts
     * @param {Set} previouslyApplied - Set of previously applied discount codes
     * @returns {Object|undefined} Best discount or undefined if none suitable
     */
    chooseBestDiscount(conflictingDiscounts, previouslyApplied = new Set()) {
        if (!conflictingDiscounts || !Array.isArray(conflictingDiscounts) || conflictingDiscounts.length === 0) {
        return undefined;
        }

        // Filter out invalid discounts
        const validDiscounts = conflictingDiscounts.filter(d =>
        d && typeof d === 'object'
        );

        if (validDiscounts.length === 0) {
        return undefined;
        }

        if (validDiscounts.length === 1) {
        return validDiscounts[0];
        }

        try {
        // Prefer previously applied discounts for stability
        const previouslyAppliedDiscount = validDiscounts.find(d =>
            previouslyApplied.has(this.getDiscountCode(d))
        );
        if (previouslyAppliedDiscount) {
            return previouslyAppliedDiscount;
        }

        // Choose by priority, then by estimated value
        return validDiscounts.reduce((best, current) => {
            if (!best) return current;
            if (!current) return best;

            const bestPriority = this.getNumericPriority(best) ?? 0;
            const currentPriority = this.getNumericPriority(current) ?? 0;

            if (currentPriority > bestPriority) return current;
            if (currentPriority < bestPriority) return best;

            // If priorities are equal, choose by estimated value
            const bestValue = this.estimateDiscountValue(best);
            const currentValue = this.estimateDiscountValue(current);

            return currentValue > bestValue ? current : best;
        });

        } catch (error) {
        this.logger.error('Error choosing best discount:', error);
        return validDiscounts[0]; // Return first valid discount as fallback
        }
    }

    // Helper methods (pure functions with defensive programming)

    /**
     * Extract numeric priority from discount object
     * @param {Object} discount - Discount object
     * @returns {number|null} Numeric priority or null if invalid
     */
    getNumericPriority(discount) {
        if (!discount || typeof discount !== 'object') {
        return null;
        }

        try {
        const priority = discount.priority;
        if (priority !== undefined && priority !== null && priority !== '') {
            const numPriority = parseInt(priority, 10);
            return isNaN(numPriority) ? null : numPriority;
        }
        return null;
        } catch (error) {
        this.logger.error('Error getting numeric priority:', error);
        return null;
        }
    }

    /**
     * Check if discount is an override discount
     * @param {Object} discount - Discount object
     * @returns {boolean} True if override discount
     */
    isOverride(discount) {
        if (!discount || typeof discount !== 'object') {
        return false;
        }

        try {
        return discount.override === true || discount.override === "1";
        } catch (error) {
        this.logger.error('Error checking override status:', error);
        return false;
        }
    }

    /**
     * Check if discount is non-combinable
     * @param {Object} discount - Discount object
     * @returns {boolean} True if non-combinable
     */
    isNonCombinable(discount) {
        if (!discount || typeof discount !== 'object') {
        return false;
        }

        try {
        return discount.non_combinable === true || discount.non_combinable === "1";
        } catch (error) {
        this.logger.error('Error checking non-combinable status:', error);
        return false;
        }
    }

    /**
     * Calculate logical priority based on discount characteristics
     * @param {Object} discount - Discount object
     * @returns {number} Logical priority (lower = higher priority)
     */
    getLogicalPriority(discount) {
        if (!discount || typeof discount !== 'object') {
        return 999; // Lowest priority for invalid discounts
        }

        try {
        const isOverride = this.isOverride(discount);
        const hasConditions = discount.conditions &&
            typeof discount.conditions === 'object' &&
            Object.keys(discount.conditions).length > 0;
        const requiresAll = discount.requirement === 'all';
        const isOrderDiscount = discount.type === 'order';

        if (isOverride) {
            return isOrderDiscount ? (hasConditions ? 10 : 12) : (hasConditions ? 1 : 3);
        } else {
            return isOrderDiscount ? (hasConditions ? 30 : 32) : (hasConditions ? 20 : 22);
        }
        } catch (error) {
        this.logger.error('Error calculating logical priority:', error);
        return 999;
        }
    }

    /**
     * Get services that a discount applies to
     * @param {Object} discount - Discount object
     * @returns {Array} Array of service identifiers
     */
    getDiscountServices(discount) {
        if (!discount || typeof discount !== 'object') {
        return [];
        }

        try {
        const services = [];

        if (discount.type === 'category') {
            if (discount.category && Array.isArray(discount.category)) {
            services.push(...discount.category);
            }

            if (discount.service && typeof discount.service === 'object') {
            const serviceValues = Object.values(discount.service).filter(v => v !== null && v !== undefined);
            services.push(...serviceValues);
            }

            // Also include service_ids and service names for more precise matching.
            // Match on the conditions key, the condition's own service_id, and the
            // condition's service name so overlap detection works regardless of which
            // of these a given promo happens to populate.
            if (discount.conditions && typeof discount.conditions === 'object') {
            Object.entries(discount.conditions).forEach(([serviceId, condition]) => {
                if (serviceId !== null && serviceId !== undefined) {
                services.push(`service_id_${serviceId}`);
                }
                if (condition && typeof condition === 'object') {
                if (condition.service_id !== null && condition.service_id !== undefined) {
                    services.push(`service_id_${condition.service_id}`);
                }
                if (condition.service) {
                    services.push(condition.service);
                }
                }
            });
            }
        } else if (discount.type === 'order') {
            services.push('ORDER_LEVEL');
        }

        // Remove duplicates and filter out null/undefined values
        return [...new Set(services.filter(s => s !== null && s !== undefined))];
        } catch (error) {
        this.logger.error('Error getting discount services:', error);
        return [];
        }
    }

    /**
     * Check if two service arrays have overlapping services
     * @param {Array} servicesA - First service array
     * @param {Array} servicesB - Second service array
     * @returns {boolean} True if there's overlap
     */
    servicesOverlap(servicesA, servicesB) {
        if (!Array.isArray(servicesA) || !Array.isArray(servicesB)) {
        return false;
        }

        if (servicesA.length === 0 || servicesB.length === 0) {
        return false;
        }

        try {
        return servicesA.some(service =>
            service !== null &&
            service !== undefined &&
            servicesB.includes(service)
        );
        } catch (error) {
        this.logger.error('Error checking service overlap:', error);
        return false;
        }
    }

    /**
     * Get normalized discount code
     * @param {Object} discount - Discount object
     * @returns {string} Uppercase discount code
     */
    getDiscountCode(discount) {
        if (!discount || typeof discount !== 'object') {
        return '';
        }

        try {
        const code = discount.promo_code || discount.id || '';
        return String(code).trim().toUpperCase();
        } catch (error) {
        this.logger.error('Error getting discount code:', error);
        return '';
        }
    }

    /**
     * Estimate the value impact of a discount
     * @param {Object} discount - Discount object
     * @returns {number} Estimated discount value
     */
    estimateDiscountValue(discount) {
        if (!discount || typeof discount !== 'object') {
        return 0;
        }

        try {
        const amount = parseFloat(discount.amount) || 0;

        switch (discount.amount_type) {
            case 'percent':
            return amount;
            case 'dollar':
            return amount;
            case 'fixed':
            return amount;
            default:
            return 0;
        }
        } catch (error) {
        this.logger.error('Error estimating discount value:', error);
        return 0;
        }
    }

    /**
     * Validate a discount object structure
     * @param {Object} discount - Discount object to validate
     * @returns {Object} Validation result with valid flag and issues array
     */
    validateDiscountObject(discount) {
        const result = {
        valid: true,
        issues: []
        };

        if (!discount) {
        result.valid = false;
        result.issues.push('Discount is null or undefined');
        return result;
        }

        if (typeof discount !== 'object') {
        result.valid = false;
        result.issues.push('Discount is not an object');
        return result;
        }

        try {
        // Check required fields
        if (!discount.id && !discount.promo_code) {
            result.issues.push('Missing required id or promo_code');
        }

        if (!discount.amount_type) {
            result.issues.push('Missing amount_type');
        }

        if (discount.amount === undefined || discount.amount === null) {
            result.issues.push('Missing amount');
        }

        // Validate amount_type
        const validAmountTypes = ['percent', 'dollar', 'fixed'];
        if (discount.amount_type && !validAmountTypes.includes(discount.amount_type)) {
            result.issues.push(`Invalid amount_type: ${discount.amount_type}`);
        }

        // Validate numeric fields
        if (discount.amount !== undefined && isNaN(parseFloat(discount.amount))) {
            result.issues.push('Amount is not a valid number');
        }

        if (discount.priority !== undefined && discount.priority !== null &&
            discount.priority !== '' && isNaN(parseInt(discount.priority, 10))) {
            result.issues.push('Priority is not a valid number');
        }

        // Validate type
        const validTypes = ['order', 'category', 'item'];
        if (discount.type && !validTypes.includes(discount.type)) {
            result.issues.push(`Invalid type: ${discount.type}`);
        }

        // Set overall validity
        result.valid = result.issues.length === 0;

        } catch (error) {
        result.valid = false;
        result.issues.push(`Validation error: ${error.message}`);
        }

        return result;
    }

    /**
     * Clean and normalize a discount object
     * @param {Object} discount - Raw discount object
     * @returns {Object|null} Normalized discount or null if invalid
     */
    normalizeDiscount(discount) {
        if (!discount || typeof discount !== 'object') {
        return null;
        }

        try {
        const normalized = {
            id: discount.id || discount.promo_code || null,
            promo_code: discount.promo_code || discount.id || null,
            title: discount.title || discount.promo_code || discount.id || 'Unnamed Discount',
            type: discount.type || 'order',
            amount: parseFloat(discount.amount) || 0,
            amount_type: discount.amount_type || 'percent',
            priority: this.getNumericPriority(discount),
            override: this.isOverride(discount),
            non_combinable: this.isNonCombinable(discount),
            min_amount: parseFloat(discount.min_amount) || 0,
            category: Array.isArray(discount.category) ? discount.category :
            (discount.category ? [discount.category] : []),
            service: discount.service || {},
            conditions: discount.conditions || {},
            requirement: discount.requirement || 'all',
            prices: discount.prices || {},
            excludes: Array.isArray(discount.excludes) ? discount.excludes :
            (discount.excludes ? [discount.excludes] : []),
            allow_multiple_category_discounts: Boolean(discount.allow_multiple_category_discounts),
            allow_multiple_order_discounts: Boolean(discount.allow_multiple_order_discounts),
            date: discount.date || null
        };

        // Validate the normalized discount
        const validation = this.validateDiscountObject(normalized);
        if (!validation.valid) {
            this.logger.warn(`Discount normalization failed for ${normalized.promo_code}:`, validation.issues);
            return null;
        }

        return normalized;
        } catch (error) {
        this.logger.error('Error normalizing discount:', error);
        return null;
        }
    }
}
